import './index.less';
import classNames from 'classnames';
import { cloneDeep, debounce } from 'lodash';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { svgPathBbox } from 'svg-path-bbox';
import svgPath from 'svgpath';
import CurPenSvg from '@/assets/cur-pen.svg';
import {
    BaseScaleView,
    BaseStagePlugin,
    getFramework,
    MoveListenerLevel,
    trackConfig,
    useActiveFrameIndex,
    useIdentity,
    useIsLongImageMode,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { getLocaleStore, useLocaleStore } from '@/i18n/index';
import { Events as VectorLayerEvents } from '@/layers/vector-layer/constants';
import { buildVectorLayer } from '@/layers/vector-layer/render';
import type { Displacement } from '@/plugins/displacement';
import {
    applyDrawFrameTarget,
    cancelCurrentDraw,
    resolveDrawFrameTarget,
    resolveDrawingFrameId,
} from '@/utils/draw-frame-kit';
import { getLastMousePoint, isHotKeyPress, type ITouchKitEvent } from '@/utils/event-kit';
import { fetchSvgContentWithCache, formatSvgPathDecimals, getVectorTextFromCache } from '@/utils/file-kit';
import { canConvertBrushToVectorLayer, convertBrushToVectorLayer } from '@/utils/layer-kit';
import {
    BaseLayerViewType,
    BrushDasharrayType,
    BrushLinecapType,
    BrushType,
    LayerType,
    PluginType,
    type IBrushDrawConfig,
    type IBrushLayerProps,
    type IExportPlugin,
    type ILayerProps,
    type IPoint,
    type IVectorLayerProps,
} from '@/version-types';
import type { BrushLayer } from '../../index';
import { canPreviewBrushInteraction } from '../../render/interaction-preview';
import {
    addVipMaterialIds,
    exportBrushPath,
    generateBrushPath,
    generateLinePath,
    getBrushBaseInfo,
    getBrushDefaultLayer,
    getDefaultBrushAttribute,
    getPathOffset,
    isArrowSvg,
    isCurveSvg,
    scaleConfig,
    updateScreenshotUrl,
} from '../../utils';
import { BrushDrawPlugin } from './brush-draw';
import BrushPreviewCanvas, { type BrushPreviewHandle } from './brush-preview-canvas';
import { adaptDrawBrushConfig } from './draw-data';
import { BrushDrawLifecycle, resetBrushDrawInteraction } from './draw-lifecycle';
import { DRAW_EVENTS } from './event-name';
import brushDrawPluginState, { getBrushLayerState, useBrushLayerState } from './states/brush-state';

function setUserSelectDisabled(disabled: boolean) {
    document.body.classList.toggle('editor-disable-user-select', disabled);
}

/**
 * 绘制矢量图形时的实时预览。
 *
 * 图层此时还没有真实宿主，无法走 Vector SVG Preview 管理器，因此直接用同一条 builder 管线
 * 同步构建 SVG，保证拖拽过程中的画面与落图后的正式渲染一致。
 *
 * @param props - 当前预览用的矢量图层数据。
 * @returns 挂载 SVG 的 Stage 节点；素材未就绪导致构建失败时返回 null。
 */
const BrushVectorPreview: React.FC<{ layer: ILayerProps }> = React.memo(({ layer }) => {
    const svgHostRef = useRef<HTMLDivElement>(null);
    // Polygon 依赖远程素材，绘制前 brushUrlPath 的预取已让同一份 SVG 文本进入缓存，这里同步取用。
    const source = getVectorTextFromCache((layer.data as IVectorLayerProps).url);
    const result = useMemo(() => buildVectorLayer(layer, source), [layer, source]);

    useLayoutEffect(() => {
        if (result) svgHostRef.current?.replaceChildren(result.svg);
    }, [result]);

    if (!result) return null;

    return (
        <BaseScaleView layer={layer} style={{ opacity: layer.opacity, overflow: 'visible', pointerEvents: 'none' }}>
            <div
                ref={svgHostRef}
                style={{
                    position: 'absolute',
                    left: result.renderBox.left,
                    top: result.renderBox.top,
                    width: result.renderBox.width,
                    height: result.renderBox.height,
                }}
            />
        </BaseScaleView>
    );
});

const DrawOperate: React.FC = React.memo(() => {
    const identity = useIdentity();
    const scale = useScale();
    const activeStageIndex = useActiveFrameIndex();
    const isLongImageMode = useIsLongImageMode();
    const framework = getFramework(identity);

    /** 第一次是否点击在画布中间 */
    const mousedownInStage = useRef(false);
    /** 绘制的鼠标点 */
    const mousePoints = useRef<IPoint[]>([]);
    /** 绘制的鼠标点在舞台上的点 */
    const mouseStagePoints = useRef<IPoint[]>([]);
    /** 绘制时的预览图层 */
    const preview = useRef<ILayerProps>();
    /** 画笔属性 */
    const drawConfig = useBrushLayerState()((state) => state.drawConfig);
    const drawConfigVersion = useBrushLayerState()((state) => state.drawConfigVersion);
    /** 渲染预览图层数据 */
    const [previewLayer, setPreviewLayer] = useState<ILayerProps | null>(null);
    const brushPreviewRef = useRef<BrushPreviewHandle>(null);
    /** 远程素材里的 path */
    const brushUrlPath = useRef('');
    /** 自定义鼠标样式 */
    const [penPoint, setPenPoint] = useState<IPoint | null>(null);
    /** 画笔实时属性 */
    const [brushData, setDrawBrushData] = useState<IBrushDrawConfig | null>(null);
    /** 当前舞台宽 */
    const currentWidth = framework.service.frame.getSizeByIndex(activeStageIndex).width;
    const isVectorLayer = useRef(false);
    const isDrawing = useRef(false);
    const isFinishing = useRef(false);
    /** 当前绘制锁定的画布索引 */
    const drawingFrameIndex = useRef<number>();
    /** 当前绘制锁定的稳定画布 ID */
    const drawingFrameId = useRef<number>();
    const mouseMoveHandle = useRef<ReturnType<typeof debounce> | null>(null);
    const vectorSettledHandler = useRef<number | null>(null);
    const drawLifecycle = useRef(new BrushDrawLifecycle());
    const { $t } = useLocaleStore();
    const drawTips = {
        tip: $t((v) => v.brushLayer.tips.moveToCanvas),
    };

    const pluginRef = useRef<HTMLDivElement>(null);

    function resetDrawInteraction() {
        isFinishing.current = false;
        if (vectorSettledHandler.current !== null) {
            framework.service.events.off(vectorSettledHandler.current);
            vectorSettledHandler.current = null;
        }
        brushPreviewRef.current?.clear();
        resetBrushDrawInteraction(
            {
                mousedownInStage,
                mousePoints,
                mouseStagePoints,
                preview,
                brushUrlPath,
                isVectorLayer,
                isDrawing,
                mouseMoveHandle,
            },
            setPreviewLayer,
            setPenPoint,
        );
        drawingFrameIndex.current = undefined;
        drawingFrameId.current = undefined;
    }

    /** 根据舞台大小，从原始配置重新适配 */

    // 窗口失焦或退出画笔时兜底恢复文字选择，避免 body 类名残留
    useEffect(() => {
        const enableUserSelect = () => {
            setUserSelectDisabled(false);
        };

        window.addEventListener('blur', enableUserSelect);
        return () => {
            enableUserSelect();
            window.removeEventListener('blur', enableUserSelect);
        };
    }, []);
    useEffect(() => {
        drawLifecycle.current.reset();
        resetDrawInteraction();
        setDrawBrushData(drawConfig ? adaptDrawBrushConfig(drawConfig, currentWidth) : null);
    }, [activeStageIndex, currentWidth, drawConfig, drawConfigVersion]);

    useEffect(() => {
        const lifecycle = drawLifecycle.current;
        return () => {
            lifecycle.reset();
            if (vectorSettledHandler.current !== null) {
                framework.service.events.off(vectorSettledHandler.current);
                vectorSettledHandler.current = null;
            }
            mouseMoveHandle.current?.cancel();
        };
    }, []);

    useEffect(() => {
        // 画笔切换，加载远程路径
        brushUrlPath.current = '';
        if (!brushData) return;
        const generation = drawLifecycle.current.currentGeneration;
        const configVersion = drawConfigVersion;
        if (brushData.url) {
            fetchSvgContentWithCache(brushData.url).then((text) => {
                if (
                    !drawLifecycle.current.isCurrent(generation) ||
                    getBrushLayerState(identity).drawConfigVersion !== configVersion
                )
                    return;
                const pathD = exportBrushPath(text);
                if (pathD) {
                    brushUrlPath.current = pathD;
                }
            });
        } else {
            brushUrlPath.current = '-';
        }
    }, [brushData, drawConfigVersion, identity]);

    useEffect(() => {
        if (!drawConfig) return;
        const handle = framework.service.hotkey.onEsc(() => {
            clearDrawConfig();
        });
        return () => {
            framework.service.events.off(handle);
        };
    }, [drawConfig]);

    useEffect(() => {
        if (!brushData) return;
        // 禁用图层选中事件，取消激活图层
        framework.service.editor.setDisablePick(true);
        framework.service.frame.delHoverId();
        framework.service.frame.setPickActiveFrame(false);
        framework.service.frame.delActiveLayer();

        const configVersion = drawConfigVersion;
        const mouseDownHandle = (e) => {
            if (getBrushLayerState(identity).drawConfigVersion !== configVersion) return;
            showDrawTip(e, true);
        };

        const mouseEnterHandler = framework.service.editor.onMouseEnter((originIndex) => {
            if (
                drawingFrameIndex.current === undefined &&
                !isDrawing.current &&
                framework.service.edgelessCanvas.isLongImageMode
            ) {
                framework.service.frame.setActiveIndex(framework.service.frame.frameIdToIndex(originIndex));
            }
        });

        if (brushData.__isQuickDraw && !isDrawing.current && isLongImageMode) {
            resolveDrawFrameIndex(getLastMousePoint());
        }

        const handle = debounce(
            (e) => {
                if (!brushData || getBrushLayerState(identity).drawConfigVersion !== configVersion) return;
                const mPoint = { x: e.x, y: e.y };
                const stageIndex = resolveDrawFrameIndex(mPoint);
                const isInStage = framework.service.transform.checkPointInFrame(mPoint, stageIndex);
                if (isInStage) {
                    if (isArrowSvg(brushData.brushType) || isCurveSvg(brushData.brushType)) {
                        setPenPoint(mPoint);
                    } else {
                        setPenPoint(null);
                    }
                } else {
                    setPenPoint(null);
                }
                showDrawTip(e);
            },
            5,
            { leading: true },
        );
        mouseMoveHandle.current = handle;

        document.addEventListener('mousedown', mouseDownHandle);
        document.addEventListener('mousemove', handle);
        return () => {
            handle.cancel();
            if (mouseMoveHandle.current === handle) mouseMoveHandle.current = null;
            framework.service.editor.setDisablePick(false);
            framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
            framework.service.events.off(mouseEnterHandler);
            document.removeEventListener('mousedown', mouseDownHandle);
            document.removeEventListener('mousemove', handle);
        };
    }, [brushData, drawConfigVersion, identity]);

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (
                !brushData ||
                !brushUrlPath.current ||
                isFinishing.current ||
                getBrushLayerState(identity).drawConfigVersion !== drawConfigVersion
            )
                return;
            let startInStage = mousedownInStage.current;
            let previewProps = preview.current;
            let stageIndex = drawingFrameIndex.current ?? framework.service.frame.getActiveIndex();
            if (e.isStart) {
                stageIndex = resolveDrawFrameIndex(e.startPoint);
                framework.service.editor.setDisablePick(true);
                startInStage = framework.service.transform.checkPointInFrame(e.startPoint, stageIndex);
                mousedownInStage.current = startInStage;
                if (startInStage) {
                    // 绘制移出画布时会经过其他 DOM，临时禁用文字选择，避免出现浏览器原生选区
                    setUserSelectDisabled(true);
                }
                preview.current = getBrushDefaultLayer(brushData);
                previewProps = preview.current;
                if (startInStage && framework.service.edgelessCanvas.isLongImageMode) {
                    drawingFrameIndex.current = stageIndex;
                    drawingFrameId.current = resolveDrawingFrameId(
                        framework.service.edgelessCanvas.isLongImageMode,
                        stageIndex,
                        (frameIndex) => framework.service.frame.indexToFrameId(frameIndex),
                    );
                }
            }
            if (e.isEnd) {
                framework.service.editor.setDisablePick(false);
                setUserSelectDisabled(false);
            }
            if (!startInStage) return;
            isDrawing.current = true;
            const mousePoint = framework.service.transform.getScenePoint(e.originEvent);
            // 判断点是否有效
            let isValidPoint = true;
            if (mousePoints.current.length) {
                const lastPoint = mousePoints.current[mousePoints.current.length - 1];
                if (lastPoint.x === mousePoint.x && lastPoint.y === mousePoint.y) {
                    isValidPoint = false;
                }
            }
            if (isValidPoint) {
                if (brushData.brushType === BrushType.Arrow || brushData.brushType === BrushType.Line) {
                    const hotKeyPress = isHotKeyPress();
                    // 绘制 90 180 的角度
                    if (hotKeyPress && mousePoints.current.length) {
                        const firstPoint = mousePoints.current[0];
                        const xLength = Math.abs(mousePoint.x - firstPoint.x);
                        const yLength = Math.abs(mousePoint.y - firstPoint.y);
                        if (xLength > yLength) {
                            mousePoint.y = firstPoint.y;
                        } else {
                            mousePoint.x = firstPoint.x;
                        }
                    }
                }
                mousePoints.current.push(mousePoint);
                mouseStagePoints.current.push(framework.service.transform.getViewportPoint(e.originEvent));
            }

            if (mousePoints.current.length === 1) return;

            if (previewProps) {
                const useScenePoint =
                    canConvertBrushToVectorLayer(brushData.brushType) ||
                    brushData.brushType === BrushType.Curve ||
                    brushData.brushType === BrushType.Arrow ||
                    brushData.brushType === BrushType.Line;
                const scaleConfigData = scaleConfig(brushData, undefined, useScenePoint ? 1 : scale);

                if (brushData.brushType !== BrushType.Line) {
                    const info = getBrushBaseInfo(
                        identity,
                        scaleConfigData,
                        useScenePoint ? mousePoints.current[0] : mouseStagePoints.current[0],
                        useScenePoint
                            ? mousePoints.current[mousePoints.current.length - 1]
                            : mouseStagePoints.current[mouseStagePoints.current.length - 1],
                    );
                    Object.assign(previewProps, {
                        width: ~~info.width,
                        height: ~~info.height,
                        left: ~~info.left,
                        top: ~~info.top,
                        rotate: info.rotate,
                    });
                    Object.assign(previewProps.data, {
                        naturalWidth: info.naturalWidth,
                        naturalHeight: info.naturalHeight,
                    });
                    // 生成绘制路径
                    (previewProps.data as IBrushLayerProps).__drawPath = generateBrushPath(
                        useScenePoint ? mousePoints.current : mouseStagePoints.current,
                        0,
                    )[brushData.brushType](brushUrlPath.current) as string;
                    // 添加预览标记
                    (previewProps.data as IBrushLayerProps).__isDrawPreview = true;
                } else {
                    const data = previewProps.data as IBrushLayerProps;
                    const startPoint = mousePoints.current[0];
                    const endPoint = mousePoints.current[mousePoints.current.length - 1];
                    const info = getBrushBaseInfo(identity, brushData!, startPoint, endPoint);
                    data.__drawPath = generateLinePath({ x: 0, y: 0 }, { x: info.width, y: 0 });
                    data.naturalWidth = info.width;
                    data.naturalHeight = 1;
                    const res = getPathOffset(data.fills, data.stroke);
                    previewProps.width = data.naturalWidth + res.offsetWidth;
                    previewProps.height = data.naturalHeight + res.offsetHeight;
                    previewProps.left = Math.floor(info.left - res.translate);
                    previewProps.top = Math.floor(info.top - res.translate);
                    previewProps.rotate = info.rotate;
                }

                // 预览只修改顶层和 data 的标量字段，不需要每个采样点深拷贝笔刷配置。
                const preLayer = { ...previewProps };
                preLayer.data = { ...preLayer.data, ...scaleConfigData } as any;
                if (brushData.brushType === BrushType.Curve) {
                    const data = preLayer.data as IBrushLayerProps;
                    const drawPath = data.__drawPath;
                    if (drawPath) {
                        const [left, top, right, bottom] = svgPathBbox(drawPath);
                        data.naturalWidth = Math.max(right - left, 1);
                        data.naturalHeight = Math.max(bottom - top, 1);
                        data.__drawPath = formatSvgPathDecimals(svgPath(drawPath).translate(-left, -top).toString());
                        delete data.__isDrawPreview;

                        const res = getPathOffset(data.fills, data.stroke);
                        preLayer.width = data.naturalWidth + res.offsetWidth;
                        preLayer.height = data.naturalHeight + res.offsetHeight;
                        preLayer.left = Math.floor(left - res.translate);
                        preLayer.top = Math.floor(top - res.translate);
                    }
                }
                const vectorPreLayer = convertBrushToVectorLayer(preLayer);
                isVectorLayer.current = !!vectorPreLayer;
                if (vectorPreLayer) {
                    brushPreviewRef.current?.clear();
                    setPreviewLayer(vectorPreLayer);
                } else {
                    // 高频输入直接更新视口 Canvas，不触发 React 图层预览和异步整层栅格化。
                    brushPreviewRef.current?.draw(preLayer);
                }
            }

            if (e.isEnd) {
                if (previewProps) {
                    const info = getBrushBaseInfo(
                        identity,
                        brushData,
                        mousePoints.current[0],
                        mousePoints.current[mousePoints.current.length - 1],
                    );
                    Object.assign(previewProps, {
                        width: ~~info.width,
                        height: ~~info.height,
                        left: ~~info.left,
                        top: ~~info.top,
                        rotate: info.rotate,
                    });
                    Object.assign(previewProps.data, {
                        naturalWidth: info.naturalWidth,
                        naturalHeight: info.naturalHeight,
                    });
                    if (brushData.brushType === BrushType.Curve) {
                        // 曲线平滑处理
                        (previewProps.data as IBrushLayerProps).__drawPath = generateBrushPath(
                            mousePoints.current,
                            // 获取抖动值
                            (currentWidth || 1242) / 200,
                        )[brushData.brushType]()!;
                    }
                    // 转成图层绘制
                    convertPreviewPropsToLayer(cloneDeep(previewProps));
                }
                // setPreviewLayer(null);
                mousedownInStage.current = false;
                mousePoints.current = [];
                mouseStagePoints.current = [];
                isDrawing.current = false;
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'brush-draw-default',
    );

    // 绘制提示
    function showDrawTip(e: any, needDestroy = false) {
        if (!brushData) return;
        const mPoint = { x: e.x, y: e.y };
        if (!framework.service.transform.checkPointInViewport(mPoint)) {
            framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
            return;
        }
        const stageIndex = resolveDrawFrameIndex(mPoint);
        const isInStage = framework.service.transform.checkPointInFrame(mPoint, stageIndex);
        if (!isInStage && !isDrawing.current) {
            if (needDestroy) {
                clearDrawConfig();
            }
            framework.service.registration.getInstance<Displacement>('Displacement')?.set({
                type: 'custom',
                x: mPoint.x,
                y: mPoint.y,
                txt: drawTips.tip,
            });
        } else {
            framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
        }
    }

    /** 仅在长图模式下按鼠标位置同步画布；无限画布保持原绘制逻辑 */
    function resolveDrawFrameIndex(point: IPoint) {
        if (drawingFrameIndex.current !== undefined) {
            return drawingFrameIndex.current;
        }
        const activeFrameIndex = framework.service.frame.getActiveIndex();
        if (!framework.service.edgelessCanvas.isLongImageMode) {
            return activeFrameIndex;
        }
        const frameIndex = framework.service.transform.getFrameIndexByMousePoint(point);
        if (frameIndex === undefined) {
            return activeFrameIndex;
        }
        if (frameIndex !== activeFrameIndex) {
            framework.service.frame.setActiveIndex(frameIndex);
        }
        return frameIndex;
    }

    function clearDrawConfig() {
        getBrushLayerState(identity).setDrawConfig(null);
    }

    function convertPreviewPropsToLayer(layer: ILayerProps) {
        const generation = drawLifecycle.current.currentGeneration;
        const data = layer.data as IBrushLayerProps;
        delete data.__isDrawPreview;
        if (!data.__drawPath) return;
        const brushType = data.brushType;
        if (brushType === BrushType.Curve) {
            const [left, top, right, bottom] = svgPathBbox(data.__drawPath);
            data.naturalWidth = Math.max(right - left, 1);
            data.naturalHeight = Math.max(bottom - top, 1);
            if (data.__drawPath) {
                // 对路径进行位移
                data.__drawPath = formatSvgPathDecimals(svgPath(data.__drawPath).translate(-left, -top).toString());
            }
            const res = getPathOffset(data.fills, data.stroke);
            layer.width = data.naturalWidth + res.offsetWidth;
            layer.height = data.naturalHeight + res.offsetHeight;
            layer.left = Math.floor(left - res.translate);
            layer.top = Math.floor(top - res.translate);
        } else if (brushType === BrushType.Line) {
            const startPoint = mousePoints.current[0];
            const endPoint = mousePoints.current[mousePoints.current.length - 1];
            const info = getBrushBaseInfo(identity, brushData!, startPoint, endPoint);
            data.__drawPath = generateLinePath({ x: 0, y: 0 }, { x: info.width, y: 0 });
            data.naturalWidth = info.width;
            data.naturalHeight = 1;
            const res = getPathOffset(data.fills, data.stroke);
            layer.width = data.naturalWidth + res.offsetWidth;
            layer.height = data.naturalHeight + res.offsetHeight;
            layer.left = Math.floor(info.left - res.translate);
            layer.top = Math.floor(info.top - res.translate);
            layer.rotate = info.rotate;
        }

        addVipMaterialIds(layer, brushData);

        const vectorLayer = convertBrushToVectorLayer(layer);
        const resultLayer = vectorLayer || layer;
        const drawFrameTarget = resolveDrawFrameTarget({
            isLongImageMode: framework.service.edgelessCanvas.isLongImageMode,
            drawingFrameId: drawingFrameId.current,
            checkFrameIsRender: (frameId) => framework.service.frame.checkFrameIsRender(frameId),
            getFrameOffsetTop: (frameId) => framework.service.frame.getFrameTransform(frameId).offsetTop,
        });
        if (drawFrameTarget.type === 'cancel') {
            cancelCurrentDraw({
                generation,
                isCurrent: (currentGeneration) => drawLifecycle.current.isCurrent(currentGeneration),
                resetInteraction: resetDrawInteraction,
                clearDrawConfig,
            });
            return;
        }
        const brushLayer = framework.service.registration.getInstance<BrushLayer>('BrushLayer');
        const drawLayerArgs =
            drawFrameTarget.type === 'target'
                ? applyDrawFrameTarget(resultLayer, drawFrameTarget)
                : { layer: resultLayer };
        const configVersion = drawConfigVersion;
        if (vectorLayer) {
            isFinishing.current = true;
            // 在 add 前监听，避免资源同步完成时漏掉正式 drawable 的交接事件。
            vectorSettledHandler.current = framework.service.events.on(
                VectorLayerEvents.DrawableSettled,
                (layerId: string) => {
                    if (layerId !== resultLayer.id) return;
                    if (vectorSettledHandler.current !== null) {
                        framework.service.events.off(vectorSettledHandler.current);
                        vectorSettledHandler.current = null;
                    }
                    if (
                        !drawLifecycle.current.isCurrent(generation) ||
                        getBrushLayerState(identity).drawConfigVersion !== configVersion
                    )
                        return;
                    isFinishing.current = false;
                    // 正式资源已写入 Canvas 对象，先同步重绘，再卸载 SVG 预览。
                    framework.service.edgelessCanvas.renderAll();
                    setPreviewLayer(null);
                    clearDrawConfig();
                },
            );
        }
        const resLayer = brushLayer.add({
            ...drawLayerArgs,
            trackFunc: trackConfig[LayerType.BRUSH_LAYER][`add_${brushType}`],
            checkFocus: true,
        });

        if (!vectorLayer && canPreviewBrushInteraction(resLayer)) {
            // 正式对象已能同步渲染平滑后的路径，立即交接，避免旧笔迹覆盖后续的拖拽画面。
            framework.service.edgelessCanvas.renderAll();
            brushPreviewRef.current?.clear();
        }

        framework.service.events.emit(DRAW_EVENTS.DRAW_END, brushType);

        if (!vectorLayer) {
            drawLifecycle.current.scheduleClose(generation, () => {
                if (getBrushLayerState(identity).drawConfigVersion === configVersion) clearDrawConfig();
            });
        }

        // 如果转成了svg, 不需要生成缩略图
        if (vectorLayer) return;
        updateScreenshotUrl(identity, resLayer.id);
    }

    if (!brushData) return null;

    return (
        <BaseStagePlugin ref={pluginRef} name="brush-draw">
            <RenderPen identity={identity} pen={penPoint} />
            <BrushPreviewCanvas ref={brushPreviewRef} identity={identity} config={brushData} />
            {previewLayer ? <BrushVectorPreview layer={previewLayer} /> : null}
        </BaseStagePlugin>
    );
});

const RenderPen: React.FC<{
    identity: string;
    pen: IPoint | null;
}> = React.memo(({ identity, pen }) => {
    const customCursor = pen ? getFramework(identity).service.transform.getViewportPoint(pen) : null;
    return (
        <div
            className={classNames('brush-draw-mask', { crosshair: !customCursor })}
            data-operate-type={BaseLayerViewType.MASK}
        >
            {customCursor && (
                <div
                    className="brush-draw-mask-pen"
                    style={{ transform: `translate(${customCursor.x}px, ${customCursor.y}px)` }}
                >
                    <img src={CurPenSvg} width={16} height={16} />
                </div>
            )}
        </div>
    );
});

const DrawPlugin: React.FC = React.memo(() => {
    const drawConfig = useBrushLayerState()((state) => state.drawConfig);
    const drawConfigVersion = useBrushLayerState()((state) => state.drawConfigVersion);
    const identity = useIdentity();
    const isInit = useRef(false);
    const framework = getFramework(identity);

    useEffect(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }
        framework.service.events.emit(DRAW_EVENTS.CONFIG_CHANGE, drawConfig);
    }, [drawConfig, drawConfigVersion]);

    /** 注册绘制快捷键 */
    useEffect(() => {
        const { $t } = getLocaleStore();
        framework.service.hotkey.register(
            ...[
                {
                    name: $t((v) => v.Text_NewLine),
                    key: 'L',
                    win: 'L',
                    mac: 'L',
                    processors: [
                        {
                            press: ['L'],
                            fn: () => {
                                console.log('L register');
                                // 绘制直线
                                getBrushLayerState(identity).setDrawConfig({
                                    brushType: BrushType.Line,
                                    fills: [
                                        {
                                            ...getDefaultBrushAttribute(),
                                            color: '#797c81ff',
                                            width: 9,
                                            linecap: BrushLinecapType.Round,
                                            dasharray: BrushDasharrayType.Solid,
                                        },
                                    ],
                                    __isQuickDraw: true,
                                } as IBrushDrawConfig);
                            },
                        },
                    ],
                },
                {
                    name: $t((v) => v.Text_NewCircle),
                    key: 'O',
                    win: 'O',
                    mac: 'O',
                    processors: [
                        {
                            press: ['O'],
                            fn: () => {
                                // 绘制圆
                                getBrushLayerState(identity).setDrawConfig({
                                    brushType: BrushType.Ellipse,
                                    stroke: getDefaultBrushAttribute(),
                                    fills: [
                                        {
                                            ...getDefaultBrushAttribute(),
                                            color: '#797c81ff',
                                        },
                                    ],
                                    __isQuickDraw: true,
                                } as IBrushDrawConfig);
                            },
                        },
                    ],
                },
                {
                    name: $t((v) => v.Text_NewRectangle),
                    key: 'R',
                    win: 'R',
                    mac: 'R',
                    processors: [
                        {
                            press: ['R'],
                            fn: () => {
                                // 绘制矩形
                                getBrushLayerState(identity).setDrawConfig({
                                    brushType: BrushType.Rect,
                                    stroke: getDefaultBrushAttribute(),
                                    fills: [
                                        {
                                            ...getDefaultBrushAttribute(),
                                            color: '#797c81ff',
                                        },
                                    ],
                                    radius: 0,
                                    __isQuickDraw: true,
                                } as IBrushDrawConfig);
                            },
                        },
                    ],
                },
            ],
        );
    }, []);

    return drawConfig ? <DrawOperate /> : null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'brush-draw',
    render() {
        return <DrawPlugin />;
    },
    createState() {
        return [brushDrawPluginState];
    },
    createInstance({ identity }) {
        return [{ key: 'BrushDrawPlugin', value: new BrushDrawPlugin(identity) }];
    },
};

export default exportPlugin;
